import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Container from '../components/common/Container';
import {
  LightBulbIcon,
  AcademicCapIcon,
  SparklesIcon,
  HeartIcon,
  ArrowTrendingUpIcon,
  UserIcon,
} from '@heroicons/react/24/outline';

const About: React.FC = () => {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const team = [
    {
      name: 'Alex Busse',
      role: 'Co-Founder / CTO',
      bio: '20 years as a UX/UI designer, software developer, and entrepreneur across fintech, e-commerce, and enterprise systems.',
      gradient: 'from-[var(--color-electric-blue)] to-[var(--color-bright-cyan)]',
    },
    {
      name: 'Blair Hamilford',
      role: 'Co-Founder',
      bio: 'Strong business strategy and operations background in education and technology sectors.',
      gradient: 'from-[var(--color-violet)] to-[var(--color-electric-blue)]',
    },
    {
      name: 'Tim van Bruggen',
      role: 'Co-Founder / Commercial Lead',
      bio: 'Extensive business and commercial strategy experience with focus on EdTech market development.',
      gradient: 'from-[var(--color-emerald)] to-[var(--color-bright-cyan)]',
    },
  ];

  const values = [
    {
      icon: LightBulbIcon,
      title: 'Feedback Drives Mastery',
      description: 'Students should see their progress as it happens — not after a term.',
      gradient: 'from-[var(--color-amber)] to-[var(--color-rose)]',
    },
    {
      icon: AcademicCapIcon,
      title: 'Learning Should Adapt',
      description: 'Every learner follows their own trajectory through the curriculum.',
      gradient: 'from-[var(--color-electric-blue)] to-[var(--color-bright-cyan)]',
    },
    {
      icon: SparklesIcon,
      title: 'AI Empowers Educators',
      description: 'Teachers gain richer insight, not extra workload.',
      gradient: 'from-[var(--color-violet)] to-[var(--color-electric-blue)]',
    },
    {
      icon: HeartIcon,
      title: 'Curriculum is Structure, Not Limit',
      description: 'The Australian curriculum provides roots; Lumen8 provides the growth.',
      gradient: 'from-[var(--color-emerald)] to-[var(--color-bright-cyan)]',
    },
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center animated-gradient-bg overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[var(--color-violet)] rounded-full filter blur-[128px] opacity-20 float-animation"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[var(--color-electric-blue)] rounded-full filter blur-[128px] opacity-20 float-animation" style={{ animationDelay: '2s' }}></div>
        </div>

        <Container size="xl" className="relative z-10">
          <motion.div
            ref={heroRef}
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={heroInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.6 }}
              className="inline-block mb-6"
            >
              <div className="glass-card px-4 py-2 rounded-full border border-[var(--color-violet)]/30">
                <span className="text-sm font-semibold text-[var(--color-violet)]">
                  Our Story
                </span>
              </div>
            </motion.div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mb-8 leading-[0.95]">
              <span className="text-white">Born from</span>
              <br />
              <span className="text-gradient">Frustration</span>
              <br />
              <span className="text-white">Built for</span>
              <br />
              <span className="text-gradient">Transformation</span>
            </h1>

            <p className="text-xl md:text-2xl text-[var(--color-light-gray)] leading-relaxed max-w-2xl mx-auto">
              We're parents, educators, and technologists who believe learning deserves better tools.
            </p>
          </motion.div>
        </Container>
      </section>

      {/* The Shared Experience */}
      <section className="py-24 bg-gradient-to-b from-[var(--color-midnight)] to-[var(--color-dark-navy)]">
        <Container>
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-black text-white mb-8">
                A <span className="text-gradient">Shared</span> Experience
              </h2>
              <div className="space-y-6 text-lg text-[var(--color-light-gray)] leading-relaxed">
                <p>
                  As parents, we've all been there — waiting <span className="text-[var(--color-electric-blue)] font-semibold">months</span> to understand
                  how our children are actually progressing at school.
                </p>
                <p>
                  Report cards arrive quarterly, offering a snapshot that tries to capture an entire term.
                  And in between? Limited visibility into day-to-day learning.
                </p>
                <p>
                  As teachers, the challenge is different but related — how do you track the progress of
                  every student across every topic, while managing the demands of a busy classroom?
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="card-premium p-10">
                <p className="text-2xl md:text-3xl font-bold text-white leading-snug mb-6">
                  "We believed there had to be a <span className="text-gradient">better way</span>"
                </p>
                <p className="text-[var(--color-light-gray)] text-lg">
                  One that gives <span className="text-[var(--color-electric-blue)] font-semibold">parents</span> visibility,
                  <span className="text-[var(--color-violet)] font-semibold"> teachers</span> insight, and
                  <span className="text-[var(--color-emerald)] font-semibold"> students</span> the support they need, when they need it.
                </p>
              </div>
              <div className="absolute -top-4 -right-4 w-32 h-32 bg-[var(--color-electric-blue)] rounded-full filter blur-3xl opacity-20"></div>
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-[var(--color-violet)] rounded-full filter blur-3xl opacity-20"></div>
            </motion.div>
          </div>
        </Container>
      </section>

      {/* The Solution */}
      <section className="py-24 bg-[var(--color-dark-navy)]">
        <Container>
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-black text-white mb-8">
                Building Something <span className="text-gradient">Better</span>
              </h2>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="glass-card p-8 md:p-12 mb-12"
            >
              <p className="text-2xl md:text-3xl font-bold text-[var(--color-electric-blue)] mb-6 leading-relaxed">
                "What if learning could provide instant feedback — just like the best digital experiences?"
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="space-y-6 text-lg text-[var(--color-light-gray)] leading-relaxed text-left md:text-center"
            >
              <p>
                We wanted to create a platform where students get help exactly when they need it.
                Where teachers can see at a glance who's thriving and who needs support.
                Where parents can stay connected to their child's learning journey.
              </p>
              <p>
                And we wanted it to work across <span className="text-white font-semibold">all subjects</span> — Mathematics, English, Science, and more —
                so that progress isn't siloed but visible in one unified view.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="mt-12"
            >
              <p className="text-3xl md:text-4xl font-black">
                <span className="text-white">That vision became </span>
                <span className="text-gradient">Lumen8</span>
              </p>
            </motion.div>
          </div>
        </Container>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-gradient-to-b from-[var(--color-dark-navy)] to-[var(--color-midnight)]">
        <Container>
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="card-premium p-10 h-full hover-glow">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[var(--color-electric-blue)] to-[var(--color-bright-cyan)] p-4 mb-6">
                  <LightBulbIcon className="w-full h-full text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Our Vision</h3>
                <p className="text-[var(--color-light-gray)] text-lg leading-relaxed">
                  To reimagine learning as a living, adaptive system that evolves with every student —
                  turning education into a personalised, data-driven journey that grows in real time.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="card-premium p-10 h-full hover-glow">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[var(--color-violet)] to-[var(--color-electric-blue)] p-4 mb-6">
                  <ArrowTrendingUpIcon className="w-full h-full text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Our Mission</h3>
                <p className="text-[var(--color-light-gray)] text-lg leading-relaxed">
                  To build an AI-powered, multi-subject learning platform aligned with the Australian curriculum
                  that delivers adaptive mastery tracking, fast feedback loops, and engaging learning experiences
                  accessible to every school and household.
                </p>
              </div>
            </motion.div>
          </div>
        </Container>
      </section>

      {/* Core Values */}
      <section className="py-24 bg-[var(--color-midnight)]">
        <Container>
          <div className="text-center max-w-3xl mx-auto mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
                Our Core <span className="text-gradient">Beliefs</span>
              </h2>
              <p className="text-xl text-[var(--color-light-gray)]">
                The principles that guide everything we build
              </p>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="card-premium p-8 h-full hover-glow transition-all duration-300 hover:scale-[1.02]">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${value.gradient} p-3 mb-6`}>
                    <value.icon className="w-full h-full text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">
                    {value.title}
                  </h3>
                  <p className="text-[var(--color-light-gray)]">
                    {value.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Team */}
      <section className="py-24 bg-gradient-to-b from-[var(--color-midnight)] to-[var(--color-dark-navy)]">
        <Container>
          <div className="text-center max-w-3xl mx-auto mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
                The <span className="text-gradient">Founding Team</span>
              </h2>
              <p className="text-xl text-[var(--color-light-gray)]">
                Combining decades of experience in technology, education, and business
              </p>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {team.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                viewport={{ once: true }}
              >
                <div className="card-premium p-8 text-center h-full hover-glow transition-all duration-300 hover:scale-[1.02]">
                  {/* Circle placeholder for photo */}
                  <div className="relative mx-auto mb-6 w-32 h-32">
                    <div className={`absolute inset-0 rounded-full bg-gradient-to-br ${member.gradient} opacity-20`}></div>
                    <div className={`absolute inset-2 rounded-full bg-gradient-to-br ${member.gradient} opacity-30`}></div>
                    <div className="absolute inset-0 rounded-full border-2 border-white/20 flex items-center justify-center">
                      <UserIcon className="w-12 h-12 text-white/60" />
                    </div>
                    {/* Decorative ring */}
                    <div className={`absolute -inset-1 rounded-full bg-gradient-to-br ${member.gradient} opacity-50 blur-sm -z-10`}></div>
                  </div>

                  <h3 className="text-xl font-bold text-white mb-2">
                    {member.name}
                  </h3>
                  <p className="text-sm font-semibold text-[var(--color-electric-blue)] mb-4">
                    {member.role}
                  </p>
                  <p className="text-[var(--color-light-gray)] text-sm leading-relaxed">
                    {member.bio}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[var(--color-dark-navy)]">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
              Ready to <span className="text-gradient">Learn More?</span>
            </h2>
            <p className="text-xl text-[var(--color-light-gray)] mb-10">
              See how Lumen8 can transform learning for your students, school, or family.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-premium text-lg px-8 py-4 inline-flex items-center gap-2">
                Get in Touch
                <ArrowTrendingUpIcon className="w-5 h-5" />
              </Link>
              <Link to="/how-it-works" className="btn-glass text-lg px-8 py-4">
                See How It Works
              </Link>
            </div>
          </motion.div>
        </Container>
      </section>
    </>
  );
};

export default About;
